import Collapse from '@material-ui/core/Collapse'
import { makeStyles } from '@material-ui/core/styles'
import React from 'react'
import { isMacOs } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import InfoIcon from '../../common/icons/Info'
import { GRIN_SCOPE_MAX_IN_ORDER } from 'consts/billingConsts'
import DazzedParagraph12 from '../../common/text/DazzedParagraph12'
import DazzedParagraph16 from '../../common/text/DazzedParagraph16'
import OrderGrinKitsItem from './OrderGrinKitsItem'
import usePlanLimits from 'hooks/usePlanLimits'
import OrderExtraInfo from './OrderExtraInfo'

const useStyles = makeStyles({
  body: {
    display: 'flex',
    flexDirection: 'column'
  },
  headerContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    columnGap: 16,
    rowGap: 8,
    width: 'fit-content',
    maxWidth: '100%',
    margin: '0 auto 8px'
  },
  subTitle: {
    fontWeight: 500
  },
  headerLearnMoreContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap'
  },
  headerInfoIcon: {
    marginRight: 8,
    width: 16,
    flexShrink: 0
  },
  headerLearnMore: {
    marginTop: 0,
    marginBottom: 0,
    fontSize: 14,
    textDecoration: 'underline',
    fontWeight: 400,
    color: 'var(--text-color-3)',
    cursor: 'pointer'
  },
  currencyPrefix: {
    fontFamily: 'Roboto',
    fontSize: 13,
    fontWeight: 400,
    marginTop: isMacOs ? 2 : 0
  },
  addScopesMini: {
    alignSelf: 'center',
    marginTop: 10,
    marginBottom: 15,
    fontSize: 15,
    fontWeight: 400,
    color: 'var(--text-color-3)',
    cursor: 'pointer'
  },
  learMoreContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center'
  },
  infoIcon: {
    marginBottom: 4,
    marginRight: 5,
    width: 16
  },
  learMore: {
    alignSelf: 'center',
    marginTop: 10,
    marginBottom: 15,
    fontSize: 14,
    textDecoration: 'underline',
    fontWeight: 400,
    color: 'var(--text-color-3)',
    cursor: 'pointer'
  },
  separator: {
    width: '100%',
    height: 1,
    backgroundColor: 'var(--border-color-7)',
    marginTop: 5,
    marginBottom: 8
  },
  note: {
    color: 'var(--text-color-6)'
  }
})

const OrderGrinKitsAmount = ({
  products,
  shippingFee,
  formErrors,
  onShowProductComparison,
  handleChangeQuantity,
  handleClickMinusQuantity,
  handleClickPlusQuantity,
  hidePrice,
  canClinicBuyScopes,
  isSubscriptionPlan,
  hasAnyAmount
}) => {
  const { t } = useTranslation()
  const classes = useStyles({ quantityError: formErrors?.quantity })
  const { grinPlanGroup } = usePlanLimits()

  return (
    <div className={classes.body}>
      {!isSubscriptionPlan && (
        <div className={classes.headerContainer}>
          <DazzedParagraph16 className={classes.subTitle} textAlign="center">
            {t('dialogs.orderScopes.scopesAmountText', { amount: GRIN_SCOPE_MAX_IN_ORDER })}
          </DazzedParagraph16>

          {products.length > 0 && (
            <div className={classes.headerLearnMoreContainer}>
              <InfoIcon className={classes.headerInfoIcon} />
              <DazzedParagraph16 className={classes.headerLearnMore} onClick={onShowProductComparison}>
                {t('dialogs.orderScopes.learnMore')}
              </DazzedParagraph16>
            </div>
          )}
        </div>
      )}

      {products.map(product => (
        <OrderGrinKitsItem
          key={product.type}
          name={product.name}
          description={product.description}
          image={product.image}
          type={product.type}
          price={product.price}
          previousPrice={product.previousPrice}
          hidePrice={hidePrice}
          quantity={product.quantity}
          maxQuantity={product.maxQuantity}
          error={product.error}
          descriptionNote={product.descriptionNote}
          helperText={product.helperText}
          productTooltip={product.productTooltip}
          onSetQuantity={quantity => handleChangeQuantity({ ...product, quantity })}
          onClickMinusQuantity={() => handleClickMinusQuantity(product)}
          onClickPlusQuantity={() => handleClickPlusQuantity(product)}
        />
      ))}

      <Collapse in={isSubscriptionPlan && products.length > 0}>
        <div className={classes.learMoreContainer}>
          <InfoIcon className={classes.infoIcon} />
          <DazzedParagraph16 className={classes.learMore} onClick={onShowProductComparison}>
            {t('dialogs.orderScopes.learnMore')}
          </DazzedParagraph16>
        </div>
      </Collapse>

      <div className={classes.separator} />
      {!canClinicBuyScopes ? (
        <DazzedParagraph12 className={classes.note}>{t('dialogs.orderScopes.manualShippingFeeNote')}</DazzedParagraph12>
      ) : (
        <OrderExtraInfo grinPlanGroup={grinPlanGroup} shippingFee={shippingFee} />
      )}
    </div>
  )
}

export default OrderGrinKitsAmount
